// Single source of truth for "what locale does this URL imply?".
// Used by both i18n init (synchronous) and the language toggle.
//
// Convention: /fr and /fr/* render French. Everything else renders English.
// (Loi 101 default kicks in only when there's no path context — e.g. SSR.)

export type SupportedLocale = 'fr' | 'en';

export function localeFromPath(pathname: string): SupportedLocale {
  if (pathname === '/fr' || pathname.startsWith('/fr/')) return 'fr';
  return 'en';
}

/**
 * EN path -> FR path, for the ONLY routes that exist in both languages.
 *
 * Everything else — /dashboard, /account, /help, /configure, /login, … — is a
 * single route that switches language through i18n and has NO /fr twin. The old
 * implementation prefixed blindly, so the toggle sent a signed-in caregiver to
 * /fr/dashboard, which has no route and rendered the 404 page. The language
 * toggle threw people out of the app.
 *
 * The FAQ cluster pages are listed explicitly because their slugs are localized
 * too — /fr/faq/living-alone-and-far-away would have 404'd just as badly.
 */
const LOCALIZED_ROUTES: ReadonlyArray<readonly [string, string]> = [
  ['/', '/fr'],
  ['/about', '/fr/about'],
  ['/contact', '/fr/contact'],
  ['/faq', '/fr/faq'],
  ['/faq/living-alone-and-far-away', '/fr/faq/parent-seul-a-distance'],
  ['/faq/memory-vision-movement', '/fr/faq/memoire-vision-mobilite'],
  ['/faq/price-trial-cancelling', '/fr/faq/prix-essai-annulation'],
];

const stripTrailingSlash = (p: string): string =>
  p.length > 1 && p.endsWith('/') ? p.slice(0, -1) : p;

/**
 * Where to navigate when switching locale. Returns the current path UNCHANGED
 * when the route has no counterpart in the target language — the caller then
 * simply changes the i18n language in place, which is what the app routes need.
 */
export function pathForLocale(currentPath: string, target: SupportedLocale): string {
  const path = stripTrailingSlash(currentPath);

  for (const [en, fr] of LOCALIZED_ROUTES) {
    if (path === en || path === fr) return target === 'fr' ? fr : en;
  }

  // No localized twin: stay put and let i18n swap the copy.
  return currentPath;
}
